export const PAYMENT_STATUS = [
  "Non Réglé",
  "Réglé",
  "En Attente",
  "Annulé",
  "Remboursé",
  "Partiellement Réglé",
];

export const PAYMENT_TYPES = [
  "En ligne",
  "Chèque",
  "Espèces",
  "Virement",
  "CB",
  "Autre",
];

export const DEFAULT_PAYMENT_FILTERS = {
  query: "",
  article: "",
  statuses: [],
  type: "",
  season: "",
  kind: "",
  authorization: "",
};

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toAmount(value) {
  const number = Number.parseFloat(
    String(value ?? 0)
      .replace(",", ".")
      .replace(/\s/g, ""),
  );

  return Number.isFinite(number) ? number : 0;
}

export function formatEuro(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(toAmount(value));
}

export function formatDateFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
}

export function todayISO() {
  const date = new Date();
  return date.toISOString().slice(0, 10);
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function matchAllTokens(haystack, query) {
  const tokens = normalizeText(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;

  const normalizedHaystack = normalizeText(haystack);

  return tokens.every((token) => normalizedHaystack.includes(token));
}

export function isPaidStatus(status) {
  return status === "Réglé";
}

export function isClosedStatus(status) {
  return ["Réglé", "Annulé", "Remboursé"].includes(status);
}

export function isPendingStatus(status) {
  return ["Non Réglé", "En Attente", "Partiellement Réglé"].includes(status);
}

export function isCancelledOrRefunded(status) {
  return ["Annulé", "Remboursé"].includes(status);
}

export function getPaymentKind(payment) {
  if (payment?.type_achat === "licence-assurance-cotisation") {
    return "licence";
  }

  if (!payment?.Article) {
    return "licence";
  }

  return "boutique";
}

export function getPaymentKindLabel(payment) {
  return getPaymentKind(payment) === "licence"
    ? "Licence / cotisation"
    : "Boutique";
}

export function getArticleName(payment) {
  return payment?.Article?.nom || "Règlement Inscription";
}

export function getUserFullName(user) {
  return `${user?.prenom || ""} ${user?.nom || ""}`.trim() || "Utilisateur";
}

export function getUserSearchText(payment, adherentsByUser = {}) {
  const user = payment?.User || {};
  const userFullName = getUserFullName(user);
  const email = user?.email || "";

  const adherents = safeArray(adherentsByUser[user?.id]);
  const adherentsText = adherents
    .map((adherent) => `${adherent?.prenom || ""} ${adherent?.nom || ""}`)
    .join(" ");

  return `${userFullName} ${email} ${adherentsText}`;
}

export function getUniqueSeasons(payments, activeSeason = "") {
  const seasons = new Set();

  safeArray(payments).forEach((payment) => {
    if (!payment?.date_achat) return;

    const date = new Date(payment.date_achat);
    if (Number.isNaN(date.getTime())) return;

    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    // Saison du 1er juillet au 30 juin
    const season = month >= 7 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
    seasons.add(season);
  });

  if (activeSeason) seasons.add(activeSeason);

  return Array.from(seasons).sort((a, b) => b.localeCompare(a, "fr"));
}

export function isWithinSeason(dateValue, season) {
  if (!season) return true;

  const [startYear, endYear] = String(season).split("-").map(Number);

  if (!startYear || !endYear) return true;

  const start = new Date(`${startYear}-07-01T00:00:00`);
  const end = new Date(`${endYear}-06-30T23:59:59`);
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return false;

  return date >= start && date <= end;
}

export function getArticleOptions(articles) {
  return safeArray(articles)
    .map((article) => ({
      id: article.id,
      label: article.nom || "Article sans nom",
      value: article.nom || "",
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

export function sortPaymentsByDate(payments) {
  return safeArray(payments)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a?.date_achat || 0).getTime();
      const dateB = new Date(b?.date_achat || 0).getTime();

      if (dateA !== dateB) return dateB - dateA;

      return Number(b?.id || 0) - Number(a?.id || 0);
    });
}

export function filterPayments(payments, filters, adherentsByUser = {}) {
  const query = String(filters?.query || "").trim();
  const article = String(filters?.article || "");
  const type = String(filters?.type || "");
  const kind = String(filters?.kind || "");
  const season = String(filters?.season || "");
  const authorization = String(filters?.authorization || "");
  const statuses = safeArray(filters?.statuses);

  return sortPaymentsByDate(payments).filter((payment) => {
    const searchable = [
      getUserSearchText(payment, adherentsByUser),
      getArticleName(payment),
      payment?.message_achat || "",
      payment?.type_reglement || "",
      payment?.statut_reglement || "",
    ].join(" ");

    const matchQuery = !query || matchAllTokens(searchable, query);

    const matchArticle =
      !article ||
      getArticleName(payment) === article ||
      (article === "Règlement Inscription" && !payment?.Article);

    const matchStatuses =
      statuses.length === 0 || statuses.includes(payment?.statut_reglement);

    const matchType = !type || payment?.type_reglement === type;

    const matchKind = !kind || getPaymentKind(payment) === kind;

    const matchSeason = !season || isWithinSeason(payment?.date_achat, season);

    const matchAuthorization =
      !authorization ||
      (authorization === "visible"
        ? !!payment?.autorisation_achat
        : !payment?.autorisation_achat);

    return (
      matchQuery &&
      matchArticle &&
      matchStatuses &&
      matchType &&
      matchKind &&
      matchSeason &&
      matchAuthorization
    );
  });
}

export function calculatePaymentsStats(payments) {
  const rows = safeArray(payments);

  let totalAmount = 0;
  let paidAmount = 0;
  let pendingAmount = 0;
  let refundedAmount = 0;

  let paidCount = 0;
  let pendingCount = 0;
  let cancelledCount = 0;
  let refundedCount = 0;
  let partialCount = 0;

  let boutiqueCount = 0;
  let licenceCount = 0;
  let authorizedCount = 0;
  let hiddenCount = 0;
  let messagesCount = 0;

  for (const payment of rows) {
    const amount = toAmount(payment?.prix_total);
    const status = payment?.statut_reglement;

    totalAmount += amount;

    if (isPaidStatus(status)) {
      paidCount += 1;
      paidAmount += amount;
    }

    if (isPendingStatus(status)) {
      pendingCount += 1;
      pendingAmount += amount;
    }

    if (status === "Partiellement Réglé") {
      partialCount += 1;
    }

    if (status === "Annulé") {
      cancelledCount += 1;
    }

    if (status === "Remboursé") {
      refundedCount += 1;
      refundedAmount += amount;
    }

    if (getPaymentKind(payment) === "licence") licenceCount += 1;
    else boutiqueCount += 1;

    if (payment?.autorisation_achat) authorizedCount += 1;
    else hiddenCount += 1;

    if (payment?.message_achat) messagesCount += 1;
  }

  return {
    total: rows.length,
    totalAmount,
    paidCount,
    paidAmount,
    pendingCount,
    pendingAmount,
    partialCount,
    cancelledCount,
    refundedCount,
    refundedAmount,
    boutiqueCount,
    licenceCount,
    authorizedCount,
    hiddenCount,
    messagesCount,
  };
}

export function getStatusBadgeClass(status) {
  const map = {
    "Non Réglé":
      "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
    Réglé:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    "En Attente":
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    Annulé:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    Remboursé:
      "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    "Partiellement Réglé":
      "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
  };

  return (
    map[status] ||
    "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
  );
}

export function canSendReminder(payment) {
  return !!payment?.User?.email && !isClosedStatus(payment?.statut_reglement);
}

export function getPaymentStatusMeta(status) {
  switch (status) {
    case "Réglé":
      return {
        badge:
          "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/20",
        row: "bg-emerald-50/70 hover:bg-emerald-100/70 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15",
        sticky:
          "bg-emerald-50/95 dark:bg-slate-900 dark:[background-image:linear-gradient(rgba(16,185,129,0.08),rgba(16,185,129,0.08))]",
      };

    case "Non Réglé":
      return {
        badge:
          "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/20",
        row: "bg-rose-50/70 hover:bg-rose-100/70 dark:bg-rose-500/10 dark:hover:bg-rose-500/15",
        sticky:
          "bg-rose-50/95 dark:bg-slate-900 dark:[background-image:linear-gradient(rgba(244,63,94,0.08),rgba(244,63,94,0.08))]",
      };

    case "En Attente":
      return {
        badge:
          "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/20",
        row: "bg-amber-50/70 hover:bg-amber-100/70 dark:bg-amber-500/10 dark:hover:bg-amber-500/15",
        sticky:
          "bg-amber-50/95 dark:bg-slate-900 dark:[background-image:linear-gradient(rgba(245,158,11,0.08),rgba(245,158,11,0.08))]",
      };

    case "Partiellement Réglé":
      return {
        badge:
          "bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-500/20",
        row: "bg-sky-50/70 hover:bg-sky-100/70 dark:bg-sky-500/10 dark:hover:bg-sky-500/15",
        sticky:
          "bg-sky-50/95 dark:bg-slate-900 dark:[background-image:linear-gradient(rgba(14,165,233,0.08),rgba(14,165,233,0.08))]",
      };

    case "Remboursé":
      return {
        badge:
          "bg-violet-100 text-violet-800 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:ring-violet-500/20",
        row: "bg-violet-50/70 hover:bg-violet-100/70 dark:bg-violet-500/10 dark:hover:bg-violet-500/15",
        sticky:
          "bg-violet-50/95 dark:bg-slate-900 dark:[background-image:linear-gradient(rgba(139,92,246,0.08),rgba(139,92,246,0.08))]",
      };

    case "Annulé":
      return {
        badge:
          "bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700/60 dark:text-slate-200 dark:ring-slate-600",
        row: "bg-slate-100/80 hover:bg-slate-200/80 dark:bg-slate-800/70 dark:hover:bg-slate-800",
        sticky: "bg-slate-100/95 dark:bg-slate-900",
      };

    default:
      return {
        badge:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
        row: "bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/50",
        sticky: "bg-white dark:bg-slate-900",
      };
  }
}

export function getReminderHistory(payment) {
  const value = payment?.rappels_paiement;

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
}

export function getReminderCount(payment) {
  return getReminderHistory(payment).length;
}

export function formatReminderDateTime(value) {
  if (!value) return "Date inconnue";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date invalide";
  }

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
